import axios from "axios";
import React, { createContext, useContext, useState } from "react";
import { hostname } from "../utils";

export interface WhitelabelSettings {
  id?: string;
  title: string;
  headTitle: string;
  faviconText: string;
  // Light mode colors
  primaryColor: string;
  secondaryColor: string;
  // Dark mode colors (optional - falls back to light mode colors if not set)
  primaryColorDark?: string;
  secondaryColorDark?: string;
  isMainSite: boolean;
  termsUrl?: string;
  privacyUrl?: string;
  supportUrl?: string;
  pricingUrl?: string;
  documentationUrl?: string;
  newCompanyWebhook?: string;
  newCompanyListId?: string;
  salesEmail: string;
  securityEmail?: string;
  ownerEmail?: string;
  employeeTermSingular?: string;
  employeeTermPlural?: string;
  webDomain?: string;
  customSubscriptionEnabled?: boolean;
  disableFreeAccounts?: boolean;
  disableEnterpriseAccounts?: boolean;
  rootPage?: string; // 'login' | 'home' - controls which page is shown at root URL
  headingFontFamily?: string; // Font family for h1-h6 headings
  bodyFontFamily?: string; // Font family for body text
  customFontUrl?: string; // Custom Google Fonts URL for additional fonts
  // Onboarding completion page customization
  onboardingCompletionContent?: string; // Custom completion page content in markdown
  publicEmployeeFilter?: string; // 'all' | 'whitelabel_only' | 'none'
  publicFolderFilter?: string; // 'all' | 'whitelabel_only' | 'none'
  showEmployeeTemplates?: boolean; // Show pre-built employee templates in create dialog (default: true for main site, false for whitelabel)
  onboardingTeamSelection?: boolean; // When enabled, users pick from templates during onboarding instead of auto-generating
  defaultPublicEmployeeIds?: string[]; // Default public employees to add to new companies instead of auto-generating
  apps?: WhitelabelApp[]; // Custom apps configured by the whitelabel owner (rendered as iframes in the user menu)
}

export interface WhitelabelApp {
  id: string;
  name: string;
  icon: string;
  // External iframe app: `url` is set. Public platform app: `appId` is set
  // (the BuilderApp id) and `url` is absent. The `/usage` response uses the
  // same shape and adds `public: true` for platform apps.
  url?: string;
  appId?: string;
  public?: boolean;
  enabled: boolean;
}

interface WhitelabelLogos {
  horizontal_logo?: {
    id: string;
    url: string;
    publicUrl: string;
  };
  horizontal_logo_dark?: {
    id: string;
    url: string;
    publicUrl: string;
  };
  square_logo?: {
    id: string;
    url: string;
    publicUrl: string;
  };
  favicon?: {
    id: string;
    url: string;
    publicUrl: string;
  };
  onboarding_hero?: {
    id: string;
    url: string;
    publicUrl: string;
  };
}

interface WhitelabelContextType {
  whitelabelSettings: WhitelabelSettings | null;
  whitelabelLogos: WhitelabelLogos | null;
  loading: boolean;
  loaded: boolean;
  loadWhitelabelByDomain: () => Promise<void>;
  resetWhitelabelSettings: () => void;
}

const WhitelabelContext = createContext<WhitelabelContextType>({
  whitelabelSettings: null,
  whitelabelLogos: null,
  loading: false,
  loaded: false,
  loadWhitelabelByDomain: async () => {},
  resetWhitelabelSettings: () => {},
});

export const useWhitelabel = () => useContext(WhitelabelContext);

export const WhitelabelProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [whitelabelSettings, setWhitelabelSettings] =
    useState<WhitelabelSettings | null>(null);
  const [whitelabelLogos, setWhitelabelLogos] =
    useState<WhitelabelLogos | null>(null);
  const [loading, setLoading] = useState(false);
  const [loaded, setLoaded] = useState(false);

  // Load whitelabel by domain on mount
  React.useEffect(() => {
    loadWhitelabelByDomain();
  }, []);

  const loadWhitelabelByDomain = async () => {
    if (loading || loaded) return; // Prevent multiple calls

    setLoading(true);

    try {
      const response = await axios.get(`${hostname}/platform-settings`);

      if (response.data.settings) {
        // Merge settings with top-level fields
        const mergedSettings = {
          ...response.data.settings,
          id: response.data.id,
          termsUrl: response.data.termsUrl,
          privacyUrl: response.data.privacyUrl,
          supportUrl: response.data.supportUrl,
          pricingUrl: response.data.pricingUrl,
          documentationUrl: response.data.documentationUrl,
          newCompanyWebhook: response.data.newCompanyWebhook,
          newCompanyListId: response.data.newCompanyListId,
          salesEmail: response.data.salesEmail,
          securityEmail: response.data.securityEmail,
          ownerEmail: response.data.ownerEmail,
          webDomain: response.data.domain,
          employeeTermSingular: response.data.employeeTermSingular,
          employeeTermPlural: response.data.employeeTermPlural,
          customSubscriptionEnabled: response.data.customSubscriptionEnabled,
          disableFreeAccounts: response.data.disableFreeAccounts,
          disableEnterpriseAccounts: response.data.disableEnterpriseAccounts,
          rootPage: response.data.rootPage || 'login', // Default to 'login' for backward compatibility
          headingFontFamily: response.data.headingFontFamily,
          bodyFontFamily: response.data.bodyFontFamily,
          customFontUrl: response.data.customFontUrl,
          // Onboarding completion customization
          onboardingCompletionContent: response.data.onboardingCompletionContent,
          // Onboarding team selection
          onboardingTeamSelection: response.data.onboardingTeamSelection,
          // Default public employees for new companies
          defaultPublicEmployeeIds: response.data.defaultPublicEmployeeIds,
          // Custom apps configured by the whitelabel owner
          apps: Array.isArray(response.data.apps) ? response.data.apps : [],
        };

        setWhitelabelSettings(mergedSettings);
        // Extract logos if available
        if (response.data.logos) {
          setWhitelabelLogos(response.data.logos);
        }
      }
      setLoaded(true);
    } catch (error: any) {
      // Only log if it's not a 404
      if (error.response?.status !== 404) {
        console.error("Error fetching whitelabel settings by domain:", error);
      }
      setLoaded(true);
    } finally {
      setLoading(false);
    }
  };

  const resetWhitelabelSettings = () => {
    setWhitelabelSettings(null);
    setWhitelabelLogos(null);
    setLoaded(false);
    // Reset document title to default
    document.title = "GTM AI | Train AI on Your Business";
  };

  return (
    <WhitelabelContext.Provider
      value={{
        whitelabelSettings,
        whitelabelLogos,
        loading,
        loaded,
        loadWhitelabelByDomain,
        resetWhitelabelSettings,
      }}
    >
      {children}
    </WhitelabelContext.Provider>
  );
};
